import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export const HELPER_BROWSER = new Set([
  "poshmark", "mercari", "depop", "grailed", "facebook",
  "meesho", "myntra", "ajio", "nykaa", "tatacliq", "olx",
]);

export const HELPER_HOST = {
  poshmark: "poshmark.com",
  mercari: "mercari.com",
  depop: "depop.com",
  grailed: "grailed.com",
  facebook: "facebook.com",
  meesho: "meesho.com",
  myntra: "myntra.com",
  ajio: "ajio.com",
  nykaa: "nykaafashion.com",
  tatacliq: "tatacliq.com",
  olx: "olx.in",
};

/** Sign-in / register pages opened on Connect. Official APIs still use OAuth auth_url. */
export const HELPER_OPEN_URL = {
  poshmark: "https://poshmark.com/signup",
  mercari: "https://www.mercari.com/signup/",
  depop: "https://www.depop.com/login/",
  grailed: "https://www.grailed.com/users/sign_up",
  facebook: "https://www.facebook.com/r.php",
  meesho: "https://supplier.meesho.com/",
  myntra: "https://www.myntra.com/login",
  ajio: "https://www.ajio.com/",
  nykaa: "https://www.nykaafashion.com/",
  tatacliq: "https://www.tatacliq.com/",
  olx: "https://www.olx.in/",
};

/** Sell / create-listing pages used from the item form helper Post. */
export const HELPER_SELL_URL = {
  poshmark: "https://poshmark.com/create-listing",
  mercari: "https://www.mercari.com/sell/",
  depop: "https://www.depop.com/products/create/",
  grailed: "https://www.grailed.com/sell",
  facebook: "https://www.facebook.com/marketplace/create/item",
  meesho: "https://supplier.meesho.com/",
  myntra: "https://www.myntra.com/",
  ajio: "https://www.ajio.com/",
  nykaa: "https://www.nykaafashion.com/",
  tatacliq: "https://www.tatacliq.com/",
  olx: "https://www.olx.in/post",
};

export const TITLE_MAX = {
  ebay: 80,
  etsy: 140,
  poshmark: 80,
  mercari: 80,
  depop: 40,
  grailed: 80,
  facebook: 99,
  olx: 70,
};

export function titleMax(channel) {
  return TITLE_MAX[channel] || 200;
}

export function helperOpenUrl(channel) {
  return HELPER_OPEN_URL[channel] || `https://www.${HELPER_HOST[channel] || "google.com"}`;
}

export function helperSellUrl(channel) {
  return HELPER_SELL_URL[channel] || helperOpenUrl(channel);
}

export function listingIdFromUrl(url, channel = "") {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/+$/, "");
    const segments = path.split("/").filter(Boolean);
    const slug = segments[segments.length - 1] || "";
    if (!slug) return "";
    if (channel === "poshmark") {
      const tokens = path.match(/[a-fA-F0-9]{24}/g);
      return (tokens?.[tokens.length - 1] || slug).slice(0, 80);
    }
    if (channel === "mercari") return slug.slice(0, 80);
    if (channel === "grailed") return (slug.match(/^\d+/)?.[0] || slug).slice(0, 80);
    if (channel === "facebook") {
      const itemIndex = segments.findIndex((segment) => segment.toLowerCase() === "item");
      const id = itemIndex >= 0
        ? segments.slice(itemIndex + 1).find((segment) => /^\d+$/.test(segment))
        : "";
      return (id || slug).slice(0, 80);
    }
    if (channel === "olx") {
      const iid = path.match(/iid-(\d+)/i)?.[1];
      const trailing = slug.match(/(\d+)$/)?.[1];
      return (iid || trailing || slug).slice(0, 80);
    }
    if (channel === "meesho") {
      const productIndex = segments.findIndex((segment) => segment.toLowerCase() === "p");
      return (productIndex >= 0 ? segments[productIndex + 1] || slug : slug).slice(0, 80);
    }
    if (channel === "depop") {
      const productIndex = segments.findIndex((segment) => segment.toLowerCase() === "products");
      const productSlug = productIndex >= 0 ? segments[productIndex + 1] || "" : "";
      if (productSlug && !["create", "edit"].includes(productSlug.toLowerCase())) return productSlug.slice(0, 80);
      return "";
    }
    const last = slug.split("-").pop() || "";
    if (/^[a-f0-9]{16,}$/i.test(last)) return last.slice(0, 80);
    if (/^\d+$/.test(slug)) return slug.slice(0, 80);
    const trailing = slug.match(/(\d{5,})$/)?.[1];
    if (trailing) return trailing.slice(0, 80);
    return slug.slice(0, 80);
  } catch {
    return "";
  }
}

/** Reject obviously unrelated Poshmark slugs like /listing/saree-… on a kurta item. */
export function listingUrlFitsTitle(url, title) {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./i, "");
    if (/(?:^|\.)amazon\.(?:com|in|co\.uk|de|ca|com\.au|fr|it|es|com\.mx)$/i.test(host)) return true;
    if (/(?:^|\.)(?:myshopify\.com|shopify\.com)$/i.test(host)) return true;
    const segments = parsed.pathname.split("/").filter(Boolean);
    const hostIsDepop = host === "depop.com" || host.endsWith(".depop.com");
    let depopSlug = hostIsDepop && segments[0] === "products" && segments[1] && !["create", "edit"].includes(segments[1].toLowerCase())
      ? segments[1]
      : "";
    const depopHandle = depopSlug.split("-")[0] || "";
    if (depopSlug && /^[a-z0-9]+_\d+$/i.test(depopHandle) && depopSlug.length > depopHandle.length + 1) {
      depopSlug = depopSlug.slice(depopHandle.length + 1);
    }
    const slug = decodeURIComponent(depopSlug || parsed.pathname.replace(/\/+$/, "").split("/").pop() || "");
    const words = slug.replace(/-[a-f0-9]{16,}$/i, "").replace(/[-_]+/g, " ").trim().toLowerCase();
    if (!words || /^[a-f0-9]+$/i.test(words.replace(/\s/g, ""))) return true;
    const want = String(title || "").toLowerCase();
    if (!want.trim()) return true;
    // Only alphabetic words can be compared with a title. A slug made of ids
    // (Mercari "m65700953099", eBay "123456") carries no title words and fits.
    const tokens = words.split(/\s+/).filter((w) => w.length >= 4 && /^[a-z]+$/.test(w));
    if (!tokens.length) return true;
    const colors = ["black", "white", "gray", "grey", "brown", "beige", "yellow", "red", "orange", "pink", "purple", "blue", "green", "silver", "gold", "navy", "maroon", "cream", "ivory", "burgundy", "charcoal", "tan"];
    const picked = (text) => {
      const found = new Set();
      colors.forEach((name) => {
        if (new RegExp(`\\b${name}\\b`).test(text)) found.add(name === "grey" ? "gray" : name);
      });
      if (/\bgolden\b/.test(text)) found.add("gold");
      return found;
    };
    const fromSlug = picked(words);
    const fromTitle = picked(want);
    if (fromSlug.size && fromTitle.size && ![...fromSlug].some((name) => fromTitle.has(name))) return false;
    return tokens.some((w) => want.includes(w));
  } catch {
    return true;
  }
}

export function openHelperSite(channel, { sell = false } = {}) {
  const url = sell ? helperSellUrl(channel) : helperOpenUrl(channel);
  const w = window.open(url, "_blank", "noopener");
  return Boolean(w);
}

/**
 * Open the marketplace create-listing page in a foreground tab. Must be called
 * directly from a click handler (before any await) so the browser treats it as
 * user-initiated and does not block the pop-up. Reuses one named tab per channel.
 */
export function openSellTab(channel) {
  const url = helperSellUrl(channel);
  try {
    const w = window.open(url, `shoptosa_sell_${channel}`, "noopener");
    return Boolean(w);
  } catch {
    return false;
  }
}

export function helperSeen() {
  return document.documentElement.getAttribute("data-shoptosa-helper") === "1";
}

export function helperId() {
  return document.documentElement.getAttribute("data-shoptosa-helper-id") || "";
}

function extensionSend(payload, ms) {
  const extId = helperId();
  const send = globalThis.chrome?.runtime?.sendMessage;
  if (!extId || typeof send !== "function") return null;
  return new Promise((resolve) => {
    let done = false;
    const finish = (value) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish({ error: "timeout" }), ms);
    try {
      send.call(globalThis.chrome.runtime, extId, payload, (res) => {
        const err = globalThis.chrome?.runtime?.lastError?.message;
        finish(err && !res ? { error: err } : (res || {}));
      });
    } catch (err) {
      finish({ error: err?.message || "helper-dead" });
    }
  });
}

function postToBridge(type, payload, resultType, ms) {
  return new Promise((resolve) => {
    let done = false;
    const onMsg = (ev) => {
      if (ev.data?.type !== resultType) return;
      done = true;
      window.removeEventListener("message", onMsg);
      resolve(ev.data || {});
    };
    window.addEventListener("message", onMsg);
    window.postMessage({ type, ...payload }, "*");
    setTimeout(() => {
      if (done) return;
      window.removeEventListener("message", onMsg);
      resolve({ error: "timeout" });
    }, ms);
  });
}

/** Ask the installed helper to open the sell form and type the pack. Does not click List. */
export async function helperPostPack(channel, pack) {
  const seen = await waitForHelper();
  if (!seen) {
    return { error: "helper-missing" };
  }
  const token = typeof localStorage !== "undefined" ? (localStorage.getItem("aura_token") || "") : "";
  const host = typeof location !== "undefined" ? location.hostname : "";
  const configured = typeof document !== "undefined" ? (document.documentElement.dataset.shoptosaApi || "") : "";
  const apiBase = configured || (host === "localhost" || host === "127.0.0.1"
    ? "http://127.0.0.1:8000/api"
    : "https://api.shoptosa.com/api");
  if (token) {
    const ping = extensionSend({ type: "TOKEN", token, apiBase }, 3000);
    if (ping) await ping;
  }
  const direct = extensionSend({ type: "POST", channel, pack: pack || {} }, 8000);
  if (direct) return direct;
  return postToBridge("SHOPTOSA_HELPER_POST", { channel, pack: pack || {} }, "SHOPTOSA_HELPER_POST_RESULT", 8000);
}

/** Ask the helper to delete a Poshmark listing. Completion arrives as SHOPTOSA_HELPER_DELISTED. */
export async function helperDelist(channel, { productId = "", url = "", handle = "" } = {}) {
  const seen = await waitForHelper();
  if (!seen) return { error: "helper-missing" };
  const token = typeof localStorage !== "undefined" ? (localStorage.getItem("aura_token") || "") : "";
  const host = typeof location !== "undefined" ? location.hostname : "";
  const configured = typeof document !== "undefined" ? (document.documentElement.dataset.shoptosaApi || "") : "";
  const apiBase = configured || (host === "localhost" || host === "127.0.0.1"
    ? "http://127.0.0.1:8000/api"
    : "https://api.shoptosa.com/api");
  if (token) {
    const ping = extensionSend({ type: "TOKEN", token, apiBase }, 3000);
    if (ping) await ping;
  }
  const payload = { productId, url, handle };
  const direct = extensionSend({ type: "DELIST", channel, ...payload }, 8000);
  if (direct) return direct;
  return postToBridge("SHOPTOSA_HELPER_DELIST", { channel, ...payload }, "SHOPTOSA_HELPER_DELIST_RESULT", 8000);
}

const CHANNEL_HOST = {
  ebay: /(?:^|\.)ebay\.(?:com|co\.uk|ca|com\.au|de|fr|it|es)$/i,
  etsy: /(?:^|\.)etsy\.com$/i,
  depop: /(?:^|\.)depop\.com$/i,
  amazon: /(?:^|\.)amazon\.(?:com|in|co\.uk|de|ca|com\.au|fr|it|es|com\.mx)$/i,
  amazon_in: /(?:^|\.)amazon\.in$/i,
  walmart: /(?:^|\.)(?:walmart\.com|myshopify\.com|shopify\.com)$/i,
  shopify: /(?:^|\.)(?:myshopify\.com|shopify\.com)$/i,
  flipkart: /(?:^|\.)flipkart\.com$/i,
  poshmark: /(?:^|\.)poshmark\.com$/i,
  mercari: /(?:^|\.)mercari\.com$/i,
  grailed: /(?:^|\.)grailed\.com$/i,
  facebook: /(?:^|\.)facebook\.com$/i,
  meesho: /(?:^|\.)meesho\.com$/i,
  myntra: /(?:^|\.)myntra\.com$/i,
  ajio: /(?:^|\.)ajio\.com$/i,
  nykaa: /(?:^|\.)nykaa(?:fashion)?\.com$/i,
  tatacliq: /(?:^|\.)tatacliq\.com$/i,
  olx: /(?:^|\.)olx\.in$/i,
};

/** Music and books stay off the fashion seller lists. */
export const HIDDEN_SELLER_CHANNELS = new Set(["discogs", "abebooks"]);

/** A listing link belongs on one marketplace. An eBay URL is not an Etsy listing. */
const SELLER_STORE_CHANNELS = new Set([
  "woocommerce", "wix", "squarespace", "square", "bonanza", "backmarket",
  "stockx", "goat", "discogs", "abebooks", "google_shopping", "pinterest",
]);

export function keepsOwnStoreListing(channel) {
  return SELLER_STORE_CHANNELS.has(channel);
}

export function listingBelongsToChannel(channel, url) {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./i, "");
    const re = CHANNEL_HOST[channel];
    if (re) return re.test(host);
    if (!SELLER_STORE_CHANNELS.has(channel)) return false;
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return false;
    return !Object.values(CHANNEL_HOST).some((pattern) => pattern.test(host));
  } catch {
    return false;
  }
}

export function listingUrlInText(channel, text) {
  const blob = String(text || "");
  const patterns = {
    ebay: /https?:\/\/(?:www\.)?ebay\.[a-z.]+\/itm\/[0-9]+/i,
    etsy: /https?:\/\/(?:www\.)?etsy\.com\/listing\/[0-9]+/i,
    depop: /https?:\/\/(?:www\.)?depop\.com\/products\/(?!(?:create|edit)(?:\/|$))[A-Za-z0-9_-]+/i,
    amazon: /https?:\/\/(?:www\.)?amazon\.com\/dp\/[A-Za-z0-9]{10}/i,
    amazon_in: /https?:\/\/(?:[\w.-]+\.)?amazon\.in\/(?:dp\/[A-Za-z0-9]{10}|skucentral)[^\s]*/i,
    poshmark: /https?:\/\/(?:www\.)?poshmark\.com\/listing\/[A-Za-z0-9_-]+/i,
    mercari: /https?:\/\/(?:www\.)?mercari\.com\/(?:us\/)?item\/[A-Za-z0-9_-]+/i,
    grailed: /https?:\/\/(?:www\.)?grailed\.com\/listings?\/[A-Za-z0-9_-]+/i,
    facebook: /https?:\/\/(?:www\.)?facebook\.com\/marketplace\/item\/[0-9]+/i,
    walmart: /https?:\/\/[a-z0-9-]+\.myshopify\.com\/products\/[A-Za-z0-9_-]+/i,
    shopify: /https?:\/\/[a-z0-9-]+\.myshopify\.com\/products\/[A-Za-z0-9_-]+/i,
    olx: /https?:\/\/(?:www\.)?olx\.in\/(?:item|ad)\/[A-Za-z0-9_-]+/i,
  };
  const pattern = patterns[channel];
  if (!pattern) return "";
  const m = blob.match(pattern);
  return m ? m[0].split("?")[0].split("#")[0] : "";
}

function waitForHelper(ms = 5000) {
  if (helperSeen()) return Promise.resolve(true);
  return new Promise((resolve) => {
    const start = Date.now();
    const id = setInterval(() => {
      if (helperSeen() || Date.now() - start >= ms) {
        clearInterval(id);
        resolve(helperSeen());
      }
    }, 100);
  });
}

export function helperVersion() {
  return document.documentElement.getAttribute("data-shoptosa-helper-version") || "";
}

export async function helperCheckListed(channel, { title = "", productId = "", handle = "" } = {}) {
  const seen = await waitForHelper();
  if (!seen) return { error: "helper-missing" };
  const direct = extensionSend({ type: "CHECK_LISTED", channel, title, productId, handle }, 8000);
  if (direct) return direct;
  return postToBridge(
    "SHOPTOSA_HELPER_CHECK_LISTED",
    { channel, title, productId, handle },
    "SHOPTOSA_HELPER_CHECK_LISTED_RESULT",
    8000,
  );
}

export function listingStatusLabel(status) {
  if (status === "listed") return "Listed";
  if (status === "ready") return "Ready";
  if (status === "sold") return "Sold";
  if (status === "delisted") return "Delisted";
  return "Not listed";
}

export async function beginConnect(c, navigate) {
  if (!c || c.native) {
    if (c?.id) navigate(`/seller/connections/${c.id}`);
    return;
  }
  if (c.connect_via === "unified") {
    try {
      const started = (await api.post(`/seller/connections/${c.id}/unified/start`)).data;
      if (started.flow === "shop") {
        toast.message(started.message || "Enter your Shopify store, then click Connect.");
        navigate(`/seller/connections/${c.id}`);
        return;
      }
      if (started.flow === "oauth" && started.auth_url) {
        toast.message(`Opening ${c.name} so you can allow Shoptosa.`);
        window.location.href = started.auth_url;
        return;
      }
      if (started.flow === "sandbox") {
        await api.post(`/seller/connections/${c.id}/unified/sandbox`, { provider: started.provider || "" });
        toast.success(`${c.name} connected through the unified API`);
        navigate(`/seller/connections/${c.id}`);
        return;
      }
      if (started.flow === "platform") {
        await api.post(`/seller/connections/${c.id}/unified/credentials`, {});
        toast.success(`${c.name} connected through the unified API`);
        navigate(`/seller/connections/${c.id}`);
        return;
      }
      navigate(`/seller/connections/${c.id}?start=1`);
      return;
    } catch (e) {
      toast.error(apiError(e));
      navigate(`/seller/connections/${c.id}`);
      return;
    }
  }
  if (HELPER_BROWSER.has(c.id) || c.connect_via === "helper") {
    openHelperSite(c.id);
    navigate(`/seller/connections/${c.id}?start=1`);
    return;
  }
  navigate(`/seller/connections/${c.id}`);
}
